import { useState, useEffect } from 'react';
import { userSession } from '../utils/userSession.js';

const API_BASE = 'https://api.driftcal.net';

const inputClass =
  'mt-1 block w-full rounded-md bg-white px-3 py-2 text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-slate-600';

function parseOrigin(raw) {
  const value = String(raw || '').trim();
  if (!value) return { error: 'Origin is required.' };
  let url;
  try {
    url = new URL(value);
  } catch {
    return { error: 'Origin must be a full URL, e.g. https://yoursite.com' };
  }
  if (url.username || url.password || url.search || url.hash) {
    return { error: 'Origin cannot include credentials, query strings, or hashes.' };
  }
  if (url.pathname && url.pathname !== '/') {
    return { error: 'Use the site address only, e.g. https://yoursite.com — not a page path.' };
  }
  if (url.hostname.includes('*')) {
    return { error: 'Wildcards are not allowed in origin.' };
  }
  const host = url.hostname.toLowerCase();
  const isLocalHttp = url.protocol === 'http:' && (host === 'localhost' || host === '127.0.0.1');
  if (url.protocol !== 'https:' && !isLocalHttp) {
    return { error: 'Origin must use https:// (http:// is only allowed for localhost).' };
  }
  return { origin: `${url.protocol}//${url.host}`.toLowerCase() };
}

function nameFromSession(user) {
  if (!user) return '';
  return `${user.firstName || ''} ${user.lastName || ''}`.trim();
}

function emailFromSession(user) {
  if (!user) return '';
  return String(user.email || user.Email || '').trim();
}

export default function ApiAccessRequest({ onOpenLogin, embedded = false, bare = false }) {
  const [user, setUser] = useState(() => userSession.getUser());
  const [form, setForm] = useState(() => {
    const current = userSession.getUser();
    return {
      name: nameFromSession(current),
      email: emailFromSession(current),
      organisation: '',
      origin: '',
      use_case: ''
    };
  });
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    const applyUser = (nextUser) => {
      setUser(nextUser);
      if (!nextUser) return;
      setForm((prev) => ({
        ...prev,
        name: prev.name || nameFromSession(nextUser),
        email: emailFromSession(nextUser) || prev.email
      }));
    };
    applyUser(userSession.getUser());
    return userSession.subscribe(applyUser);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!user?.sessionToken) {
      setError('Sign in with a DriftCal account to request API access.');
      return;
    }

    const parsed = parseOrigin(form.origin);
    if (parsed.error) {
      setError(parsed.error);
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API_BASE}/v1/access-requests`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-session-token': user.sessionToken
        },
        body: JSON.stringify({
          name: form.name.trim(),
          organisation: form.organisation.trim(),
          origin: parsed.origin,
          use_case: form.use_case.trim()
        })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || 'Failed to submit request');
      }
      setResult(data);
    } catch (err) {
      setError(err.message || 'Failed to submit request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const body = (
    <>
          {result ? (
            <div className="mt-6 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-4 text-sm text-emerald-900">
              <p className="font-semibold">Request received</p>
              <p className="mt-1">
                Thanks. We&apos;ll email you if a key is issued. Keep an eye on the
                address on your DriftCal account.
              </p>
            </div>
          ) : !user ? (
            <div className="mt-6 rounded-xl border border-slate-200 bg-slate-50 px-4 py-4">
              <p className="text-sm text-slate-700">
                Sign in (or create an account) to request API access. That way the key is
                tied to you and you can rotate or delete it from your dashboard.
              </p>
              <button
                type="button"
                onClick={() => onOpenLogin && onOpenLogin('Sign in to request API access')}
                className="mt-4 inline-flex w-full items-center justify-center rounded-md bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white hover:bg-slate-700"
              >
                Sign in
              </button>
            </div>
          ) : !user.sessionToken ? (
            <div className="mt-6 rounded-xl border border-amber-200 bg-amber-50 px-4 py-4 text-sm text-amber-950">
              Sign out and sign in again so we can link this request to your account.
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="mt-6 space-y-4">
              {error && (
                <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
                  {error}
                </div>
              )}

              <div>
                <label htmlFor="api-name" className="block text-sm font-medium text-slate-800">
                  Name *
                </label>
                <input
                  id="api-name"
                  name="name"
                  type="text"
                  required
                  maxLength={255}
                  value={form.name}
                  onChange={handleChange}
                  autoComplete="name"
                  className={inputClass}
                />
              </div>

              <div>
                <label htmlFor="api-email" className="block text-sm font-medium text-slate-800">
                  Email *
                </label>
                <input
                  id="api-email"
                  name="email"
                  type="email"
                  required
                  readOnly
                  maxLength={255}
                  value={form.email}
                  autoComplete="email"
                  className={`${inputClass} bg-slate-50 text-slate-600`}
                />
                <p className="mt-1 text-xs text-slate-500">From your DriftCal account.</p>
              </div>

              <div>
                <label htmlFor="api-organisation" className="block text-sm font-medium text-slate-800">
                  Organisation
                </label>
                <input
                  id="api-organisation"
                  name="organisation"
                  type="text"
                  maxLength={255}
                  value={form.organisation}
                  onChange={handleChange}
                  autoComplete="organization"
                  className={inputClass}
                />
              </div>

              <div>
                <label htmlFor="api-origin" className="block text-sm font-medium text-slate-800">
                  Website origin *
                </label>
                <input
                  id="api-origin"
                  name="origin"
                  type="url"
                  required
                  maxLength={255}
                  value={form.origin}
                  onChange={handleChange}
                  placeholder="https://yoursite.com"
                  className={inputClass}
                />
                <p className="mt-1 text-xs leading-5 text-slate-500">
                  The website that will use the API, for example <code>https://yoursite.com</code>.
                  Leave off anything after the domain.
                </p>
              </div>

              <div>
                <label htmlFor="api-use-case" className="block text-sm font-medium text-slate-800">
                  What will you use it for?
                </label>
                <textarea
                  id="api-use-case"
                  name="use_case"
                  rows={4}
                  maxLength={4000}
                  value={form.use_case}
                  onChange={handleChange}
                  placeholder="e.g. Show upcoming drift events on our club site"
                  className={inputClass}
                />
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="inline-flex w-full items-center justify-center rounded-md bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-emerald-500 disabled:cursor-not-allowed disabled:bg-slate-400"
              >
                {submitting ? 'Sending…' : 'Submit request'}
              </button>
            </form>
          )}
    </>
  );

  if (bare) {
    return <div className="[&>:first-child]:mt-0">{body}</div>;
  }

  const card = (
    <div className="rounded-3xl bg-white p-6 shadow-xl sm:p-8">
      {!embedded && (
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          Developers
        </p>
      )}
      {embedded ? (
        <h2 className="text-2xl font-bold text-slate-900">Request API access</h2>
      ) : (
        <h1 className="mt-1 text-2xl font-bold text-slate-900">Request API access</h1>
      )}
      <p className="mt-2 text-sm leading-6 text-slate-600">
        You need a DriftCal account so the key is tied to you. After it is issued,
        rotate or delete it from Home on your dashboard.
      </p>
      {body}
    </div>
  );

  if (embedded) return card;

  return (
    <div className="relative isolate px-4 py-8 sm:px-6 lg:px-8">
      <div className="mx-auto w-full max-w-xl">{card}</div>
    </div>
  );
}
